import React from 'react';
import { CheckboxInput, NumberInput, RangeInput, SelectInput } from '../../../components/Input';

export default function AdvancedOptions({ options, onNestedChange }) {
  return (
    <div className="space-y-6">
      {/* Intro section */}
      <div className="bg-accent/5 rounded-xl p-4 border border-accent/20">
        <h4 className="text-base text-foreground font-semibold mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Compression overview
        </h4>
        <div className="text-sm text-foreground space-y-2">
          <p><strong className="text-foreground">Transform options:</strong> Best for models with redundant geometry (CAD models, architectural scenes). Moderate impact on file size.</p>
          <p><strong className="text-foreground">Draco settings:</strong> Powerful compression for complex meshes with lots of vertices (characters, organic shapes). High impact on file size.</p>
          <p><strong className="text-foreground">Texture compression:</strong> Essential for models with large images/textures (photogrammetry, detailed materials). Highest impact on file size.</p>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Need a deep-dive?&nbsp;
          <a href="/docs/support#advanced" target="_blank" rel="nofollow" className="underline text-accent hover:text-accent-hover">Read the advanced articles</a>.
        </p>
      </div>

      <div className="bg-background-soft rounded-xl p-5 border border-border/50">
        <h4 className="text-base text-foreground font-semibold mb-4">Transform options</h4>
        <div className="grid grid-cols-2 gap-4">
            <CheckboxInput
              id="deduplicate"
              label="Remove duplicate vertices"
              checked={options.transforms.deduplicate}
              onChange={(e) => onNestedChange("transforms", "deduplicate", e.target.checked)}
              tooltip="Eliminate exact overlapping vertices to reduce model size."
            />
            <CheckboxInput
              id="removeUnused"
              label="Remove unused vertices"
              checked={options.transforms.removeUnused}
              onChange={(e) => onNestedChange("transforms", "removeUnused", e.target.checked)}
              tooltip="Prune vertices that are not contributing to any geometry."
            />
            <RangeInput
              id="simplify"
              label="Geometry simplification"
              value={options.transforms.simplify}
              onChange={(e) => onNestedChange("transforms", "simplify", parseFloat(e.target.value))}
              tooltip="Simplify geometry to reduce the number of vertices. Higher values reduce detail."
            />
            <CheckboxInput
              id="instance"
              label="Instance meshes"
              checked={options.transforms.instance}
              onChange={(e) => onNestedChange("transforms", "instance", e.target.checked)}
              tooltip="Enable mesh instancing to reduce draw calls."
            />
            <CheckboxInput
              id="flatten"
              label="Flatten nodes"
              checked={options.transforms.flatten}
              onChange={(e) => onNestedChange("transforms", "flatten", e.target.checked)}
              tooltip="Flatten the scene graph by merging node hierarchies."
            />
            <CheckboxInput
              id="merge"
              label="Merge meshes"
              checked={options.transforms.merge}
              onChange={(e) => onNestedChange("transforms", "merge", e.target.checked)}
              tooltip="Combine separate meshes to reduce draw calls."
            />
            <CheckboxInput
              id="weld"
              label="Weld vertices"
              checked={options.transforms.weld}
              onChange={(e) => onNestedChange("transforms", "weld", e.target.checked)}
              tooltip="Merge nearby vertices based on proximity."
            />
          </div>
        </div>

        <div className="bg-background-soft rounded-xl p-5 border border-border/50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base text-foreground font-semibold">Draco settings</h4>
            <CheckboxInput
              id="dracoEnabled"
              label="Enabled"
              checked={options.draco.enabled}
              onChange={(e) => onNestedChange("draco", "enabled", e.target.checked)}
              tooltip=""
            />
          </div>
          {options.draco.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <NumberInput
                id="compressionLevel"
                label="Compression level"
                value={options.draco.compressionLevel}
                onChange={(e) => onNestedChange("draco", "compressionLevel", parseInt(e.target.value, 10))}
                min={1}
                max={10}
                step={1}
                tooltip="Level between 1 and 10. Higher values reduce size at the expense of quality."
              />
              <NumberInput
                id="quantizationBits"
                label="Quantization bits"
                value={options.draco.quantizationBits}
                onChange={(e) => onNestedChange("draco", "quantizationBits", parseInt(e.target.value, 10))}
                min={8}
                max={16}
                step={1}
                tooltip={
                  <div>
                    <div><strong>Like image quality settings:</strong></div>
                    <div className="mt-1.5">
                      <span className="text-warning">Lower (8-10):</span> Smaller files, subtle quality loss
                    </div>
                    <div>
                      <span className="text-success">Higher (14-16):</span> More detail, larger files
                    </div>
                    <div className="mt-1.5 text-accent">
                      <strong>Recommended:</strong> 14 for most models
                    </div>
                  </div>
                }
              />
            </div>
          )}
        </div>

        <div className="bg-background-soft rounded-xl p-5 border border-border/50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base text-foreground font-semibold">Texture compression</h4>
            <CheckboxInput
              id="textureEnabled"
              label="Enabled"
              checked={options.texture.enabled}
              onChange={(e) => onNestedChange("texture", "enabled", e.target.checked)}
              tooltip=""
            />
          </div>
          {options.texture.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <SelectInput
                id="textureFormat"
                label="Image format"
                value={options.texture.format}
                onChange={(e) => onNestedChange("texture", "format", e.target.value)}
                options={[
                  { value: "jpeg", label: "JPEG" },
                  { value: "png", label: "PNG" },
                  { value: "webp", label: "WebP" },
                  { value: "avif", label: "AVIF" }
                ]}
                tooltip={
                  <div>
                    <div className="mb-2">
                      <span className="text-warning font-bold">JPEG:</span> Smallest files, good for photos
                    </div>
                    <div className="mb-2">
                      <span className="text-success font-bold">PNG:</span> Lossless quality, supports transparency
                    </div>
                    <div className="mb-2">
                      <span className="text-info font-bold">WebP:</span> Modern format, smaller than PNG
                    </div>
                    <div>
                      <span className="text-accent font-bold">AVIF:</span> Best compression, limited browser support
                    </div>
                  </div>
                }
              />
              <NumberInput
                id="resize"
                label="Max dimensions (px)"
                value={options.texture.resize[0]}
                onChange={(e) => onNestedChange("texture", "resize", [
                  parseInt(e.target.value, 10),
                  parseInt(e.target.value, 10)
                ])}
                min={64}
                max={4096}
                step={64}
                tooltip={
                  <div>
                    <div className="mb-2">
                      <strong>Resizes large textures to this maximum size</strong>
                    </div>
                    <div className="mb-1.5">
                      <span className="text-success">Higher (2048+):</span> Sharp detail, larger files
                    </div>
                    <div className="mb-1.5">
                      <span className="text-info">Medium (1024-2048):</span> Good for most 3D models
                    </div>
                    <div>
                      <span className="text-warning">Lower (512):</span> Smaller files, may look blurry
                    </div>
                  </div>
                }
              />
              <NumberInput
                id="quality"
                label="Quality (1-100)"
                value={options.texture.quality}
                onChange={(e) => onNestedChange("texture", "quality", parseInt(e.target.value, 10))}
                min={1}
                max={100}
                step={1}
                tooltip={
                  <div>
                    <div className="mb-1.5">
                      <span className="text-success">Higher (80-100):</span> Preserve detail, larger files
                    </div>
                    <div className="mb-1.5">
                      <span className="text-accent font-bold">Medium (60-80):</span> Best balance for most textures
                    </div>
                    <div>
                      <span className="text-warning">Lower (30-60):</span> Very small files, may show artifacts
                    </div>
                  </div>
                }
              />
            </div>
          )}
        </div>
    </div>
  );
} 